import { useState, useEffect } from 'react';
import { Download, X, Share } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    if (standalone) return;

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa_dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 3 * 24 * 60 * 60 * 1000) return;

    // Detect iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    if (ios) {
      // Show iOS guide after a delay
      const timer = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(timer);
    }

    // Android/Desktop: listen for beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShowBanner(true), 2000);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }

    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const result = await deferredPrompt.userChoice;
    if (result.outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem('pwa_dismissed', Date.now().toString());
  };

  if (isStandalone || !showBanner) return null;

  // iOS Install Guide Modal
  if (showIOSGuide) {
    return (
      <div className="fixed inset-0 bg-black/70 z-[100] flex items-end justify-center p-4" onClick={handleDismiss}>
        <div
          className="w-full max-w-md bg-card border border-border rounded-t-lg p-6 space-y-4 animate-in slide-in-from-bottom"
          onClick={(e) => e.stopPropagation()}
          data-testid="ios-install-guide"
        >
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold">Install Iron Dad</h3>
            <button onClick={handleDismiss} className="text-muted-foreground hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-blue-400">1</span>
              </div>
              <p className="text-sm text-muted-foreground pt-1">
                Tap the <Share className="w-4 h-4 inline text-blue-400" /> <strong className="text-white">Share</strong> button at the bottom of Safari
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-blue-400">2</span>
              </div>
              <p className="text-sm text-muted-foreground pt-1">
                Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong>
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-blue-400">3</span>
              </div>
              <p className="text-sm text-muted-foreground pt-1">
                Tap <strong className="text-white">"Add"</strong> — Iron Dad will appear on your home screen!
              </p>
            </div>
          </div>
          <Button onClick={handleDismiss} className="w-full bg-primary text-white rounded-sm font-bold uppercase tracking-wider">
            Got It
          </Button>
        </div>
      </div>
    );
  }

  // Install Banner
  return (
    <div
      className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-card border border-border rounded-sm p-4 shadow-lg z-[90] animate-in slide-in-from-bottom"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm bg-primary/20 flex items-center justify-center flex-shrink-0">
          <Download className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold">Install Iron Dad</h4>
          <p className="text-xs text-muted-foreground mt-0.5">Add to your home screen for the full app experience</p>
        </div>
        <button onClick={handleDismiss} className="text-muted-foreground hover:text-white flex-shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
      <Button
        data-testid="pwa-install-button"
        onClick={handleInstall}
        className="w-full mt-3 bg-primary text-white hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider text-sm h-10"
      >
        {isIOS ? 'How to Install' : 'Install App'}
      </Button>
    </div>
  );
};

export default PWAInstallPrompt;
